/* __next_internal_client_entry_do_not_use__ Tabs auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Badge } from "@faire/web--source/slate/Badge";
import { BadgeVariant } from "@faire/web--source/slate/Badge/types";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { useStateWithDebounce } from "@faire/web--source/ui/hooks/useStateWithDebounce";
import isUndefined from "lodash/isUndefined";
import * as React from "react";
import styled, { css } from "styled-components";
import { Spacer } from "../../spacing";
import { responsiveMediaQueries } from "../../Theme/responsiveMediaQueries";
import { HIGH_VIS_FILTER, highVisSlateComponent } from "../../utils/highVisSlateComponent";
/**
 * @trackcomponent
 * @deprecated use Tabs from slate/Tabs
 * @deprecatedBy "@faire/web--source/slate/Tabs/Tabs#Tabs"
 * @link [Docs](https://slate.faire.team/slate/component/tabs)
 */ export var Tabs = function(props) {
    var className = props.className, activeTab = props.activeTab, actions = props.actions, tabs = props.tabs, dataTestId = props["data-test-id"], onScroll = props.onScroll;
    var tabsRef = React.useRef(null);
    var _React_useState = _sliced_to_array(React.useState(false), 2), isTabsInView = _React_useState[0], setIsTabsInView = _React_useState[1];
    var observer = React.useMemo(function() {
        return new IntersectionObserver(function(param) {
            var _param = _sliced_to_array(param, 1), entry = _param[0];
            var _entry_isIntersecting;
            return setIsTabsInView((_entry_isIntersecting = entry === null || entry === void 0 ? void 0 : entry.isIntersecting) !== null && _entry_isIntersecting !== void 0 ? _entry_isIntersecting : false);
        });
    }, []);
    React.useEffect(function() {
        if (tabsRef.current) {
            observer.observe(tabsRef.current);
        }
        return function() {
            return observer.disconnect();
        };
    }, [
        observer
    ]);
    var tabRefs = React.useMemo(function() {
        return props.tabs.reduce(function(acc, tab) {
            acc[tab.value] = /*#__PURE__*/ React.createRef();
            return acc;
        }, {});
    }, [
        props.tabs
    ]);
    var scrollToTab = React.useCallback(function(tabRef) {
        if (props.autoScrollIntoView && tabRef && isTabsInView) {
            var _tabRef_current;
            (_tabRef_current = tabRef.current) === null || _tabRef_current === void 0 ? void 0 : _tabRef_current.scrollIntoView({
                inline: "nearest",
                block: "nearest"
            });
        }
    }, [
        isTabsInView,
        props.autoScrollIntoView
    ]);
    // We want to use layout effect here to make sure the view is painted
    // before the tab is scrolled into view, and subsequently scroll everytime
    // the active tab changes
    React.useLayoutEffect(function() {
        scrollToTab(tabRefs[props.activeTab]);
    }, [
        props.activeTab,
        tabRefs,
        scrollToTab
    ]);
    var setActiveTab = function(event) {
        var _event_currentTarget_firstChild, _props_tabs_find;
        var tabLabel = (_event_currentTarget_firstChild = event.currentTarget.firstChild) === null || _event_currentTarget_firstChild === void 0 ? void 0 : _event_currentTarget_firstChild.textContent;
        var newTab = (_props_tabs_find = props.tabs.find(function(tab) {
            return tab.label === tabLabel;
        })) === null || _props_tabs_find === void 0 ? void 0 : _props_tabs_find.value;
        if (newTab !== undefined) {
            props.onTabChange(newTab);
        }
    };
    var DEBOUNCE_DELAY = 500;
    var _useStateWithDebounce = useStateWithDebounce([
        0,
        undefined
    ], DEBOUNCE_DELAY), setScrollMeasurements = _useStateWithDebounce.setValue, debouncedScrollMeasurements = _useStateWithDebounce.debouncedValue;
    var onHorizontalScroll = function() {
        var _tabsRef_current, _tabsRef_current1, _tabsRef_current2;
        if (!onScroll) {
            return;
        }
        var scrollWidth = (_tabsRef_current = tabsRef.current) === null || _tabsRef_current === void 0 ? void 0 : _tabsRef_current.scrollWidth; // width of the entire element (including overflow).
        var scrollLeft = (_tabsRef_current1 = tabsRef.current) === null || _tabsRef_current1 === void 0 ? void 0 : _tabsRef_current1.scrollLeft;
        var clientWidth = (_tabsRef_current2 = tabsRef.current) === null || _tabsRef_current2 === void 0 ? void 0 : _tabsRef_current2.clientWidth; // width not including overflow.
        if (isUndefined(scrollWidth) || isUndefined(scrollLeft) || isUndefined(clientWidth)) {
            return;
        }
        var start = scrollLeft / scrollWidth;
        var end = (scrollLeft + clientWidth) / scrollWidth;
        setScrollMeasurements([
            start,
            end
        ]);
    };
    React.useEffect(function() {
        if (!onScroll) {
            return;
        }
        var _debouncedScrollMeasurements = _sliced_to_array(debouncedScrollMeasurements, 2), start = _debouncedScrollMeasurements[0], end = _debouncedScrollMeasurements[1];
        if (!isUndefined(start) && !isUndefined(end)) {
            onScroll(start, end);
        }
    // onScroll dependency keeps re-triggering when setActiveTab is called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        debouncedScrollMeasurements
    ]);
    if (tabs.length === 0) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(TabsWrapper, {
        className: className,
        onScroll: onHorizontalScroll,
        ref: tabsRef,
        "data-test-id": dataTestId,
        children: [
            /*#__PURE__*/ _jsx(Row, {
                children: tabs.map(function(tab) {
                    var CustomTabWrapper = tab.customTabWrapper;
                    var tabMarkup = /*#__PURE__*/ _jsx(Tab, {
                        tabRef: tabRefs[tab.value],
                        active: tab.value === activeTab,
                        onClick: setActiveTab,
                        tab: tab,
                        as: props.tabAs
                    }, tab.label);
                    return CustomTabWrapper ? /*#__PURE__*/ _jsx(CustomTabWrapper, {
                        children: tabMarkup
                    }, tab.label) : tabMarkup;
                })
            }),
            actions ? actions.map(function(action) {
                return /*#__PURE__*/ _jsxs(ActionWrapper, {
                    onClick: action.onClick,
                    "data-test-id": action.label,
                    children: [
                        action.icon ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                action.icon,
                                /*#__PURE__*/ _jsx(Spacer, {
                                    size: 4
                                })
                            ]
                        }) : null,
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraphSansMedium",
                            color: Core.text.subdued,
                            children: action.label
                        })
                    ]
                }, action.label);
            }) : null
        ]
    });
};
var Tab = function(param) {
    var active = param.active, onClick = param.onClick, tab = param.tab, tabRef = param.tabRef, as = param.as;
    var amount = tab.amount, label = tab.label, showZeroAmount = tab.showZeroAmount, pillColour = tab.pillColour, notificationDot = tab.notificationDot, badgeBackgroundColour = tab.badgeBackgroundColour, badgeContentColour = tab.badgeContentColour;
    var tabTextVariant = active ? "paragraphSansMedium" : "paragraphSansRegular";
    var showAmount = amount !== undefined && (showZeroAmount || amount !== 0);
    return /*#__PURE__*/ _jsx(TabSpacer, {
        ref: tabRef,
        children: /*#__PURE__*/ _jsxs(TabWrapper, {
            type: "button",
            "data-test-id": label,
            $active: active,
            onClick: onClick,
            children: [
                /*#__PURE__*/ _jsx(Label, {
                    variant: tabTextVariant,
                    forwardedAs: as !== null && as !== void 0 ? as : "p",
                    children: label
                }),
                showAmount ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: 4
                        }),
                        /*#__PURE__*/ _jsx(Badge, {
                            backgroundColor: badgeBackgroundColour !== null && badgeBackgroundColour !== void 0 ? badgeBackgroundColour : pillColour,
                            contentColor: badgeContentColour,
                            content: amount,
                            variant: BadgeVariant.Notification,
                            notificationDot: notificationDot
                        })
                    ]
                }) : null
            ]
        })
    });
};
var Label = styled(Typography).withConfig({
    componentId: "sc-39e718ad-0"
})([
    "white-space:nowrap;"
]);
var TabsWrapper = styled.div.withConfig({
    componentId: "sc-39e718ad-1"
})([
    "display:flex;justify-content:space-between;box-sizing:border-box;width:100%;height:52px;border-bottom:1px solid ",
    ";overflow-y:auto;",
    "{scrollbar-width:none;-ms-overflow-style:none;::-webkit-scrollbar{width:0;height:0;}}"
], Core.border.muted, responsiveMediaQueries.tabletAndBelow);
var ActionWrapper = styled.button.withConfig({
    componentId: "sc-39e718ad-2"
})([
    "display:flex;align-items:center;margin:0 12px;height:100%;cursor:pointer;color:",
    ";"
], Core.text.subdued);
var TabWrapper = styled.button.withConfig({
    componentId: "sc-39e718ad-3"
})([
    "display:flex;align-items:center;height:100%;cursor:pointer;border-bottom:",
    ";",
    ""
], function(props) {
    return props.$active ? css([
        "1px solid ",
        ""
    ], Core.border.subdued) : "unset";
}, highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT));
var TabSpacer = styled.div.withConfig({
    componentId: "sc-39e718ad-4"
})([
    "height:100%;padding:0 24px;"
]);
